import { useState, useEffect } from 'react'
import { Link2, Save, Plus, Trash2, Copy, Check, Upload, ChevronUp, ChevronDown, ArrowLeft, ArrowRight, CheckCircle2, Lock } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import cadastroPublicoConfigService from '../../services/cadastroPublicoConfigService'

const NATIVOS_LABEL = {
  nome: 'Nome completo',
  cpf_cnpj: 'CPF/CNPJ',
  telefone: 'WhatsApp/Telefone',
  email: 'E-mail',
  endereco: 'Endereço',
  numero: 'Número',
  complemento: 'Complemento',
  bairro: 'Bairro',
  cidade: 'Cidade',
  estado: 'Estado',
  cep: 'CEP',
  inscricao_estadual: 'Inscrição Estadual',
  observacoes: 'Observações',
}

const NATIVOS_ADICIONAVEIS = Object.keys(NATIVOS_LABEL).filter(id => id !== 'nome')

const TIPOS_PERGUNTA = [
  { id: 'texto', label: 'Texto curto' },
  { id: 'multipla_escolha', label: 'Múltipla escolha' },
  { id: 'upload', label: 'Upload de arquivo' },
]

const CAMPO_NOME_PADRAO = { id: 'campo-nome', origem: 'nativo', campo_nativo: 'nome', label: NATIVOS_LABEL.nome, obrigatorio: true }

const LGPD_TEXTO_PADRAO = 'Em respeito à sua privacidade e em total conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018), informamos que os dados fornecidos neste formulário serão armazenados de forma segura e utilizados unicamente para viabilizar seu atendimento e o envio de informações pertinentes a este cadastro. Garantimos a não cessão dos seus dados a terceiros sem prévio consentimento.'

const DEFAULT_CONFIG = {
  titulo: '',
  subtitulo: '',
  banner_url: '',
  cor_primaria: '#1E40AF',
  mensagem_sucesso: 'Cadastro realizado com sucesso!',
  campos: [CAMPO_NOME_PADRAO],
  perguntas_por_pagina: 0,
  termos_lgpd_texto: LGPD_TEXTO_PADRAO,
  termos_lgpd_obrigatorio: true,
  is_ativo: true,
}

const STEPS = [
  { label: 'Link & Identidade' },
  { label: 'Campos & Perguntas' },
  { label: 'Paginação & LGPD' },
  { label: 'Revisão & Publicação' },
]

function ensureNome(campos) {
  const lista = campos && campos.length ? campos : []
  if (lista.some(c => c.origem === 'nativo' && c.campo_nativo === 'nome')) return lista
  return [CAMPO_NOME_PADRAO, ...lista]
}

export default function CadastroPublico() {
  const { organizadorAtivo } = useOrganizador()
  const [config, setConfig] = useState(DEFAULT_CONFIG)
  const [linkCadastro, setLinkCadastro] = useState('')
  const [step, setStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [savingLink, setSavingLink] = useState(false)
  const [message, setMessage] = useState('')
  const [messageLink, setMessageLink] = useState('')
  const [copiado, setCopiado] = useState(false)
  const [uploadingBanner, setUploadingBanner] = useState(false)
  const [messageBanner, setMessageBanner] = useState('')

  useEffect(() => {
    if (organizadorAtivo?.id) loadConfig()
  }, [organizadorAtivo])

  const loadConfig = async () => {
    try {
      const response = await cadastroPublicoConfigService.getConfig(organizadorAtivo.id)
      setConfig({
        titulo: response.titulo || '',
        subtitulo: response.subtitulo || '',
        banner_url: response.banner_url || '',
        cor_primaria: response.cor_primaria || '#1E40AF',
        mensagem_sucesso: response.mensagem_sucesso || DEFAULT_CONFIG.mensagem_sucesso,
        campos: ensureNome(response.campos),
        perguntas_por_pagina: response.perguntas_por_pagina ?? 0,
        termos_lgpd_texto: response.termos_lgpd_texto || LGPD_TEXTO_PADRAO,
        termos_lgpd_obrigatorio: response.termos_lgpd_obrigatorio ?? true,
        is_ativo: response.is_ativo ?? true,
      })
      setLinkCadastro(response.link_cadastro || '')
    } catch (error) {
      console.error('Erro ao carregar configuração:', error)
    }
  }

  const handleSaveConfig = async () => {
    try {
      setLoading(true)
      const configLimpa = {
        ...config,
        campos: config.campos.map(c => c.tipo === 'multipla_escolha'
          ? { ...c, opcoes: (c.opcoes || []).map(o => o.trim()).filter(Boolean) }
          : c),
      }
      await cadastroPublicoConfigService.saveConfig(organizadorAtivo.id, configLimpa)
      setMessage('Configurações salvas com sucesso!')
      setTimeout(() => setMessage(''), 3000)
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Erro ao salvar configurações')
      setTimeout(() => setMessage(''), 3000)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveLink = async () => {
    try {
      setSavingLink(true)
      const response = await cadastroPublicoConfigService.saveLinkCadastro(organizadorAtivo.id, linkCadastro)
      setLinkCadastro(response.link_cadastro)
      setMessageLink('Link salvo com sucesso!')
      setTimeout(() => setMessageLink(''), 3000)
    } catch (error) {
      setMessageLink(error.response?.data?.detail || 'Erro ao salvar link')
      setTimeout(() => setMessageLink(''), 3000)
    } finally {
      setSavingLink(false)
    }
  }

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) {
      setMessageBanner('Imagem excede o limite de 5MB')
      setTimeout(() => setMessageBanner(''), 3000)
      return
    }
    try {
      setUploadingBanner(true)
      const response = await cadastroPublicoConfigService.uploadBanner(organizadorAtivo.id, file)
      setConfig(prev => ({ ...prev, banner_url: response.banner_url }))
      setMessageBanner('Banner enviado! Clique em "Salvar configurações" para aplicar.')
      setTimeout(() => setMessageBanner(''), 4000)
    } catch (error) {
      setMessageBanner(error.response?.data?.detail || 'Erro ao enviar banner')
      setTimeout(() => setMessageBanner(''), 3000)
    } finally {
      setUploadingBanner(false)
      e.target.value = ''
    }
  }

  const linkPublico = linkCadastro ? `${window.location.origin}/cadastro/${linkCadastro}` : ''

  const copiarLink = () => {
    if (!linkPublico) return
    navigator.clipboard.writeText(linkPublico)
    setCopiado(true)
    setTimeout(() => setCopiado(false), 2000)
  }

  const addCampoNativo = (campoId) => {
    if (!campoId) return
    setConfig(prev => ({
      ...prev,
      campos: [...prev.campos, { id: crypto.randomUUID(), origem: 'nativo', campo_nativo: campoId, label: NATIVOS_LABEL[campoId], obrigatorio: false }],
    }))
  }

  const addPerguntaCustomizada = () => {
    setConfig(prev => ({
      ...prev,
      campos: [...prev.campos, { id: crypto.randomUUID(), origem: 'customizada', tipo: 'texto', label: '', obrigatorio: false, opcoes: [] }],
    }))
  }

  const removeCampo = (id) => {
    setConfig(prev => ({ ...prev, campos: prev.campos.filter(c => c.id !== id) }))
  }

  const updateCampo = (id, field, value) => {
    setConfig(prev => ({
      ...prev,
      campos: prev.campos.map(c => {
        if (c.id !== id) return c
        const atualizado = { ...c, [field]: value }
        // Ao trocar pra múltipla escolha, já começa com 2 opções em branco (como Forms/Tally)
        if (field === 'tipo' && value === 'multipla_escolha' && !(c.opcoes || []).length) {
          atualizado.opcoes = ['', '']
        }
        return atualizado
      }),
    }))
  }

  const addOpcao = (id) => {
    setConfig(prev => ({
      ...prev,
      campos: prev.campos.map(c => c.id === id ? { ...c, opcoes: [...(c.opcoes || []), ''] } : c),
    }))
  }

  const updateOpcaoTexto = (id, index, valor) => {
    setConfig(prev => ({
      ...prev,
      campos: prev.campos.map(c => c.id === id ? { ...c, opcoes: (c.opcoes || []).map((o, i) => i === index ? valor : o) } : c),
    }))
  }

  const removeOpcao = (id, index) => {
    setConfig(prev => ({
      ...prev,
      campos: prev.campos.map(c => c.id === id ? { ...c, opcoes: (c.opcoes || []).filter((_, i) => i !== index) } : c),
    }))
  }

  const moveCampo = (index, direction) => {
    setConfig(prev => {
      const nova = [...prev.campos]
      const alvo = index + direction
      if (alvo < 0 || alvo >= nova.length) return prev
      ;[nova[index], nova[alvo]] = [nova[alvo], nova[index]]
      return { ...prev, campos: nova }
    })
  }

  const camposNativosUsados = config.campos.filter(c => c.origem === 'nativo').map(c => c.campo_nativo)
  const camposNativosDisponiveis = NATIVOS_ADICIONAVEIS.filter(id => !camposNativosUsados.includes(id))
  const totalPerguntasCustomizadas = config.campos.filter(c => c.origem === 'customizada').length
  const isUltimoStep = step === STEPS.length - 1

  const irParaProximo = () => setStep(s => Math.min(s + 1, STEPS.length - 1))
  const irParaAnterior = () => setStep(s => Math.max(s - 1, 0))

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl px-4 sm:px-6 py-4 sm:py-6">
        <div className="flex items-center gap-3">
          <Link2 className="w-7 h-7 text-white" />
          <div>
            <h1 className="text-xl sm:text-3xl font-bold text-white">Link de Cadastro</h1>
            <p className="text-blue-100 mt-1 text-sm sm:text-base">Formulário público para captação de novos clientes</p>
          </div>
        </div>
      </div>

      {/* Stepper */}
      <div className="bg-white rounded-xl border border-gray-200 px-4 sm:px-6 py-4">
        <div className="flex items-center">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center flex-1 last:flex-none">
              <button type="button" onClick={() => setStep(i)} className="flex items-center gap-2 group">
                <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                  i === step ? 'bg-blue-600 text-white' : i < step ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400 group-hover:bg-gray-200'
                }`}>
                  {i < step ? <Check size={14} /> : i + 1}
                </span>
                <span className={`hidden sm:inline text-sm font-medium ${i === step ? 'text-gray-900' : 'text-gray-500'}`}>{s.label}</span>
              </button>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 sm:mx-3 ${i < step ? 'bg-blue-300' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>
        <p className="sm:hidden text-sm font-medium text-gray-900 mt-3">{STEPS[step].label}</p>
      </div>

      {/* Passo 1: Link & Identidade */}
      {step === 0 && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Link público</h3>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 flex items-center border border-gray-300 rounded-lg overflow-hidden">
                <span className="px-3 py-2 bg-gray-50 text-gray-500 text-sm whitespace-nowrap">/cadastro/</span>
                <input type="text" value={linkCadastro}
                  onChange={(e) => setLinkCadastro(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                  className="flex-1 px-2 py-2 outline-none text-sm" placeholder="meu-haras" />
              </div>
              <button onClick={handleSaveLink} disabled={savingLink || !linkCadastro}
                className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap text-sm font-medium">
                {savingLink ? 'Salvando...' : 'Salvar link'}
              </button>
            </div>
            {messageLink && <p className="text-sm text-blue-600">{messageLink}</p>}
            {linkPublico && (
              <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                <span className="text-sm text-blue-700 flex-1 truncate">{linkPublico}</span>
                <button onClick={copiarLink} className="text-blue-600 hover:text-blue-800 flex-shrink-0">
                  {copiado ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Identidade</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Título</label>
                <input type="text" className="input-rematei" value={config.titulo}
                  onChange={(e) => setConfig({ ...config, titulo: e.target.value })} placeholder="Cadastro de Cliente" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cor primária</label>
                <input type="color" className="w-full h-10 border border-gray-300 rounded-lg"
                  value={config.cor_primaria} onChange={(e) => setConfig({ ...config, cor_primaria: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Subtítulo</label>
                <input type="text" className="input-rematei" value={config.subtitulo}
                  onChange={(e) => setConfig({ ...config, subtitulo: e.target.value })}
                  placeholder="Preencha seus dados para se cadastrar" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Banner (opcional)</label>
                <div className="flex items-center gap-3 flex-wrap">
                  {config.banner_url && <img src={config.banner_url} alt="Banner" className="h-16 rounded-lg border border-gray-200 object-cover" />}
                  <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-sm text-gray-700">
                    <Upload size={16} />
                    {uploadingBanner ? 'Enviando...' : config.banner_url ? 'Trocar imagem' : 'Fazer upload'}
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={uploadingBanner} onChange={handleBannerUpload} />
                  </label>
                  {config.banner_url && (
                    <button type="button" onClick={() => setConfig(prev => ({ ...prev, banner_url: '' }))} className="text-sm text-red-600 hover:text-red-700">
                      Remover
                    </button>
                  )}
                </div>
                {messageBanner && <p className="text-xs text-blue-600 mt-1">{messageBanner}</p>}
                <p className="text-xs text-gray-400 mt-1">JPG, PNG ou WEBP até 5MB</p>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Mensagem de sucesso</label>
                <textarea className="input-rematei" rows={2} value={config.mensagem_sucesso}
                  onChange={(e) => setConfig({ ...config, mensagem_sucesso: e.target.value })} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Passo 2: Campos & Perguntas — lista única e reordenável */}
      {step === 1 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 space-y-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Campos & Perguntas</h3>
              <p className="text-sm text-gray-600">Use as setas pra definir a ordem exata em que tudo aparece no formulário.</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <select
                value=""
                onChange={(e) => addCampoNativo(e.target.value)}
                disabled={camposNativosDisponiveis.length === 0}
                className="input-rematei text-sm py-1.5 disabled:opacity-50"
              >
                <option value="">+ Campo do cadastro</option>
                {camposNativosDisponiveis.map(id => (
                  <option key={id} value={id}>{NATIVOS_LABEL[id]}</option>
                ))}
              </select>
              <button onClick={addPerguntaCustomizada}
                className="flex items-center gap-1 px-3 py-1.5 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 text-sm font-medium whitespace-nowrap">
                <Plus size={15} /> Pergunta
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {config.campos.map((item, index) => {
              const isNome = item.origem === 'nativo' && item.campo_nativo === 'nome'
              return (
                <div key={item.id} className={`border rounded-lg p-3 space-y-2 ${isNome ? 'border-blue-200 bg-blue-50/40' : 'border-gray-200'}`}>
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                    <div className="flex sm:flex-col items-center justify-center gap-0.5 flex-shrink-0">
                      <button type="button" onClick={() => moveCampo(index, -1)} disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-25 disabled:hover:text-gray-400">
                        <ChevronUp size={16} />
                      </button>
                      <span className="text-xs text-gray-400 w-4 text-center">{index + 1}</span>
                      <button type="button" onClick={() => moveCampo(index, 1)} disabled={index === config.campos.length - 1}
                        className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-25 disabled:hover:text-gray-400">
                        <ChevronDown size={16} />
                      </button>
                    </div>

                    {item.origem === 'nativo' ? (
                      <div className="flex-1 flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600 whitespace-nowrap">
                          Campo do cadastro
                        </span>
                        <span className="text-sm font-medium text-gray-800">{item.label}</span>
                      </div>
                    ) : (
                      <input type="text" className="input-rematei flex-1" placeholder="Pergunta"
                        value={item.label} onChange={(e) => updateCampo(item.id, 'label', e.target.value)} />
                    )}

                    {item.origem === 'customizada' && (
                      <select className="input-rematei sm:w-48" value={item.tipo}
                        onChange={(e) => updateCampo(item.id, 'tipo', e.target.value)}>
                        {TIPOS_PERGUNTA.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                      </select>
                    )}

                    {isNome ? (
                      <span className="p-2 text-gray-300" title="Nome sempre faz parte do cadastro">
                        <Lock size={16} />
                      </span>
                    ) : (
                      <button onClick={() => removeCampo(item.id)} className="text-red-500 hover:text-red-700 px-2">
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>

                  {item.origem === 'customizada' && item.tipo === 'multipla_escolha' && (
                    <div className="pl-0 sm:pl-9 space-y-2">
                      {(item.opcoes || []).map((opcao, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <span className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center text-xs font-bold bg-gray-100 text-gray-600">
                            {String.fromCharCode(65 + idx)}
                          </span>
                          <input type="text" className="input-rematei flex-1" placeholder={`Opção ${String.fromCharCode(65 + idx)}`}
                            value={opcao} onChange={(e) => updateOpcaoTexto(item.id, idx, e.target.value)} />
                          <button type="button" onClick={() => removeOpcao(item.id, idx)}
                            disabled={(item.opcoes || []).length <= 1}
                            className="text-red-400 hover:text-red-600 disabled:opacity-25 disabled:hover:text-red-400 px-1">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      ))}
                      <button type="button" onClick={() => addOpcao(item.id)}
                        className="flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-800 px-1">
                        <Plus size={13} /> Adicionar opção
                      </button>
                    </div>
                  )}

                  <label className={`flex items-center gap-1 text-xs cursor-pointer ${isNome ? 'text-gray-400' : 'text-gray-700'}`}>
                    <input type="checkbox" checked={item.obrigatorio} disabled={isNome}
                      onChange={(e) => updateCampo(item.id, 'obrigatorio', e.target.checked)} />
                    Obrigatório
                  </label>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Passo 3: Paginação & LGPD */}
      {step === 2 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 space-y-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Paginação</h3>
            <p className="text-sm text-gray-600">Quantos campos/perguntas aparecem por página do formulário. Deixe 0 pra mostrar tudo numa página só.</p>
          </div>
          <div className="sm:w-64">
            <label className="block text-sm font-medium text-gray-700 mb-1">Itens por página</label>
            <input type="number" min={0} className="input-rematei" value={config.perguntas_por_pagina}
              onChange={(e) => setConfig({ ...config, perguntas_por_pagina: Math.max(0, parseInt(e.target.value) || 0) })} />
          </div>

          <div className="border-t border-gray-100 pt-4">
            <h3 className="text-lg font-medium text-gray-900">Termos & LGPD</h3>
            <p className="text-sm text-gray-600 mb-3">Texto exibido com uma caixa de aceite antes do envio do cadastro.</p>
            <textarea className="input-rematei" rows={5} value={config.termos_lgpd_texto}
              onChange={(e) => setConfig({ ...config, termos_lgpd_texto: e.target.value })} />
            <label className="flex items-center gap-2 mt-3 cursor-pointer w-fit">
              <input type="checkbox" checked={config.termos_lgpd_obrigatorio}
                onChange={(e) => setConfig({ ...config, termos_lgpd_obrigatorio: e.target.checked })} />
              <span className="text-sm text-gray-800">Exigir aceite antes de enviar o cadastro</span>
            </label>
          </div>
        </div>
      )}

      {/* Passo 4: Revisão & Publicação */}
      {step === 3 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Revisão</h3>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-500">Link público</p>
              <p className="text-sm font-medium text-gray-900 truncate">{linkPublico || 'Não definido'}</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-500">Campos do cadastro</p>
              <p className="text-sm font-medium text-gray-900">{camposNativosUsados.length} campo(s)</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-500">Perguntas personalizadas</p>
              <p className="text-sm font-medium text-gray-900">{totalPerguntasCustomizadas} pergunta(s)</p>
            </div>
          </div>

          <label className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2.5 cursor-pointer w-fit">
            <input type="checkbox" checked={config.is_ativo} onChange={(e) => setConfig({ ...config, is_ativo: e.target.checked })} />
            <span className="text-sm text-gray-800">Formulário ativo (aceita novas submissões)</span>
          </label>

          {!linkCadastro && <p className="text-sm text-amber-600">Defina e salve o link público no passo 1 antes de publicar.</p>}

          <div className="flex items-center gap-3 pt-2">
            <button onClick={handleSaveConfig} disabled={loading}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium">
              <CheckCircle2 size={16} /> {loading ? 'Salvando...' : 'Salvar e publicar'}
            </button>
            {message && <p className="text-sm text-blue-600">{message}</p>}
          </div>
        </div>
      )}

      {/* Navegação entre passos */}
      <div className="flex items-center justify-between">
        <button onClick={irParaAnterior} disabled={step === 0}
          className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-40 text-sm font-medium">
          <ArrowLeft size={16} /> Voltar
        </button>
        <div className="flex items-center gap-3">
          {!isUltimoStep && (
            <button onClick={handleSaveConfig} disabled={loading}
              className="flex items-center gap-1.5 px-4 py-2 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 disabled:opacity-50 text-sm font-medium">
              <Save size={15} /> Salvar
            </button>
          )}
          {!isUltimoStep && (
            <button onClick={irParaProximo} className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
              Próximo <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
      {!isUltimoStep && message && <p className="text-sm text-blue-600 text-right">{message}</p>}
    </div>
  )
}
